import { useEffect, useMemo, useRef, useState } from 'react';
import { Calculator, Clock3, Gauge, PackageOpen, Tag, X } from 'lucide-react';
import { calculateEstimate, numberValue, scaleBatchProduction } from '../lib/pricing';

function startingValues(product, profiles) {
  const fallbackProfile = profiles.find((profile) => profile.isDefault) || profiles[0];
  return {
    name: product?.name || '',
    sku: product?.sku || '',
    category: product?.category || '',
    notes: product?.notes || '',
    profileId: product?.profileId || fallbackProfile?.id || '',
    filamentGrams: numberValue(product?.filamentGrams),
    printHours: numberValue(product?.printHours),
    printMinutes: numberValue(product?.printMinutes),
    quantity: Math.max(1, numberValue(product?.quantity, 1)),
    averagePowerWatts: product?.averagePowerWatts ?? '',
    laborMinutes: numberValue(product?.laborMinutes),
    additionalCost: numberValue(product?.additionalCost),
    targetMargin: product?.targetMargin ?? fallbackProfile?.targetMargin ?? '',
  };
}

export default function ProductForm({ product, profiles, saving, onClose, onSave }) {
  const [values, setValues] = useState(() => startingValues(product, profiles));
  const [error, setError] = useState('');
  const quantityScaleBasis = useRef(null);
  const isEditing = Boolean(product?.id);
  const selectedProfile = profiles.find((profile) => profile.id === values.profileId) || profiles[0] || null;
  const preview = useMemo(() => calculateEstimate(selectedProfile, values), [selectedProfile, values]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, saving]);

  const update = (field) => (event) => setValues((current) => ({ ...current, [field]: event.target.value }));
  const beginQuantityEdit = () => {
    quantityScaleBasis.current = {
      quantity: values.quantity,
      filamentGrams: values.filamentGrams,
      printHours: values.printHours,
      printMinutes: values.printMinutes,
    };
  };
  const updateQuantity = (event) => {
    const rawQuantity = event.target.value;
    if (rawQuantity === '') {
      setValues((current) => ({ ...current, quantity: '' }));
      return;
    }
    setValues((current) => {
      const basis = quantityScaleBasis.current || {
        quantity: current.quantity,
        filamentGrams: current.filamentGrams,
        printHours: current.printHours,
        printMinutes: current.printMinutes,
      };
      if (!quantityScaleBasis.current) quantityScaleBasis.current = basis;
      return { ...current, ...scaleBatchProduction(basis, rawQuantity, basis.quantity) };
    });
  };
  const finishQuantityEdit = () => {
    const basisSnapshot = quantityScaleBasis.current;
    setValues((current) => {
      const basis = basisSnapshot || current;
      const finalQuantity = Math.max(1, Math.floor(numberValue(current.quantity, numberValue(basis.quantity, 1))));
      return { ...current, ...scaleBatchProduction(basis, finalQuantity, basis.quantity) };
    });
    quantityScaleBasis.current = null;
  };
  const updateProfile = (event) => {
    const profile = profiles.find((item) => item.id === event.target.value);
    setValues((current) => ({
      ...current,
      profileId: event.target.value,
      targetMargin: numberValue(profile?.targetMargin, current.targetMargin),
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!values.name.trim()) return setError('Give this product a name.');
    if (!selectedProfile) return setError('Create a cost profile before saving a catalog product.');
    if (numberValue(values.targetMargin) < 0 || numberValue(values.targetMargin) >= 95) return setError('Target margin must be between 0% and 94.99%.');

    try {
      await onSave({
        name: values.name.trim(),
        sku: values.sku.trim(),
        category: values.category.trim(),
        notes: values.notes.trim(),
        profileId: selectedProfile.id,
        profileName: selectedProfile.name || 'Cost profile',
        filamentGrams: Math.max(0, numberValue(values.filamentGrams)),
        printHours: Math.max(0, numberValue(values.printHours)),
        printMinutes: Math.min(59, Math.max(0, numberValue(values.printMinutes))),
        quantity: Math.max(1, Math.floor(numberValue(values.quantity, 1))),
        averagePowerWatts: Math.max(0, numberValue(values.averagePowerWatts)),
        laborMinutes: Math.max(0, numberValue(values.laborMinutes)),
        additionalCost: Math.max(0, numberValue(values.additionalCost)),
        targetMargin: numberValue(values.targetMargin),
      });
    } catch (saveError) {
      setError(saveError?.message || 'The catalog product could not be saved.');
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <section className="profile-modal entity-modal" role="dialog" aria-modal="true" aria-labelledby="product-modal-title">
        <header className="profile-modal__header">
          <div>
            <p className="eyebrow">Product catalog</p>
            <h2 id="product-modal-title">{isEditing ? 'Edit reusable product' : 'Create a reusable product'}</h2>
            <p>Save the production assumptions for an item you quote or print repeatedly.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} disabled={saving} aria-label="Close product form"><X size={20} /></button>
        </header>

        <form className="profile-form entity-form" onSubmit={submit}>
          <fieldset>
            <legend><PackageOpen size={17} /> Product identity</legend>
            <div className="entity-form__grid entity-form__grid--identity">
              <label className="field"><span className="field__heading">Product name</span><span className="field__control"><input autoFocus value={values.name} onChange={update('name')} placeholder="Name this product" /></span></label>
              <label className="field"><span className="field__heading">SKU <small>Optional</small></span><span className="field__control"><input value={values.sku} onChange={update('sku')} placeholder="Enter an internal identifier" /></span></label>
              <label className="field"><span className="field__heading">Category</span><span className="field__control"><input value={values.category} onChange={update('category')} placeholder="Enter a category" /></span></label>
              <label className="field entity-form__wide"><span className="field__heading">Notes <small>Optional</small></span><span className="field__control"><input value={values.notes} onChange={update('notes')} placeholder="Packaging, finish, license, or customer notes" /></span></label>
            </div>
          </fieldset>

          <fieldset>
            <legend><Tag size={17} /> Pricing foundation</legend>
            <div className="entity-form__grid">
              <label className="field"><span className="field__heading">Cost profile</span><span className="field__control field__control--select"><select value={values.profileId} onChange={updateProfile}>{profiles.map((profile) => <option value={profile.id} key={profile.id}>{profile.name}{profile.isDefault ? ' — Default' : ''}</option>)}</select></span></label>
              <label className="field"><span className="field__heading">Target margin</span><span className="field__control unit-field"><input type="number" min="0" max="94.99" step="0.1" value={values.targetMargin} onChange={update('targetMargin')} /><b>%</b></span></label>
              <label className="field"><span className="field__heading">Batch quantity <small>Scales filament and time</small></span><span className="field__control unit-field"><input type="number" min="1" step="1" value={values.quantity} onFocus={beginQuantityEdit} onChange={updateQuantity} onBlur={finishQuantityEdit} /><b>pcs</b></span></label>
            </div>
          </fieldset>

          <fieldset>
            <legend><Clock3 size={17} /> Production assumptions</legend>
            <div className="entity-form__grid">
              <label className="field"><span className="field__heading">Filament used</span><span className="field__control unit-field"><input type="number" min="0" step="0.1" value={values.filamentGrams} onChange={update('filamentGrams')} /><b>g</b></span></label>
              <label className="field"><span className="field__heading">Print hours</span><span className="field__control unit-field"><input type="number" min="0" step="1" value={values.printHours} onChange={update('printHours')} /><b>hr</b></span></label>
              <label className="field"><span className="field__heading">Print minutes</span><span className="field__control unit-field"><input type="number" min="0" max="59" step="1" value={values.printMinutes} onChange={update('printMinutes')} /><b>min</b></span></label>
              <label className="field"><span className="field__heading">Average power</span><span className="field__control unit-field"><input type="number" min="0" step="1" value={values.averagePowerWatts} onChange={update('averagePowerWatts')} /><b>W</b></span></label>
              <label className="field"><span className="field__heading">Hands-on labor</span><span className="field__control unit-field"><input type="number" min="0" step="1" value={values.laborMinutes} onChange={update('laborMinutes')} /><b>min</b></span></label>
              <label className="field"><span className="field__heading">Additional batch cost</span><span className="field__control unit-field"><b>$</b><input type="number" min="0" step="0.01" value={values.additionalCost} onChange={update('additionalCost')} /></span></label>
            </div>
          </fieldset>

          <aside className="entity-form__preview" aria-label="Product pricing preview">
            <span><Calculator size={21} /></span>
            <div><small>Break-even per piece</small><strong>${preview.costPerPiece.toFixed(2)}</strong></div>
            <div><small>Recommended per piece</small><strong>${preview.recommendedPricePerPiece.toFixed(2)}</strong></div>
            <div><small>Projected batch profit</small><strong>${preview.totalProfit.toFixed(2)}</strong></div>
            <Gauge size={18} />
          </aside>

          {error && <p className="form-status form-status--error" role="alert">{error}</p>}
          <footer className="profile-modal__actions">
            <button className="button button--ghost" type="button" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={saving}>{saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create product'}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}
